import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy, OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild
} from '@angular/core';
import { CmsService, Page, Product, ScriptLoader } from '@spartacus/core';
import { combineLatest, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { CurrentProductService } from '@spartacus/storefront';
import { DOCUMENT, isPlatformBrowser, NgClass } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { CustomBaseStore, CustomCategory, LiveChatAgentConfig } from '../../../../models';
import { ChatActionsEnum } from '../../../../enums';
import { BaseStoreService, CategoryService, ChatService, TrackingService } from '../../../../services';
import { areSegmentsEqual, waitForElm } from '../../../../utils';
import { SdChatComponent } from '@seb/web-design-system/core';
import { TranslateService } from '@ngx-translate/core';

// @dynamic
@Component({
  selector: 'wc-live-chat',
  templateUrl: './live-chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdChatComponent, NgClass]
})
export class LiveChatComponent implements OnDestroy, OnInit, AfterViewInit {
  liveChatAgentConfig: LiveChatAgentConfig;
  embeddedSvc: any;
  showChatToUser: boolean;
  isMidlyOpen = false;
  liveChatObserver: MutationObserver;
  chatActionsEnum = ChatActionsEnum;
  messageLen = 0;
  keypressed = false;
  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private scriptLoader: ScriptLoader,
    private baseStoreService: BaseStoreService,
    private cmsService: CmsService,
    private categoryService: CategoryService,
    private currentProductService: CurrentProductService,
    private router: Router,
    private translateService: TranslateService,
    private trackingService: TrackingService,
    private chatService: ChatService,
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) protected platformId: any,
    @Inject(DOCUMENT) private document: Document
  ) {}

  @ViewChild('chatComponent', { read: ElementRef })
  chatComponent: ElementRef;

  @HostListener('document:setUrlSourcePage', ['$event'])
  setUrlSourcePage(event) {
    if (this.embeddedSvc) {
      this.embeddedSvc.settings.extraPrechatFormDetails[0].value = event.detail?.urlSourcePage;
      event.detail.callback();
    }
  }

  ngOnInit(): void {
    this.chatService.openChatEvent.subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.document.querySelector('.helpButtonEnabled')?.dispatchEvent(new Event('click'));
      }
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const baseStore = this.baseStoreService.baseStore;
      const page$ = this.cmsService.getCurrentPage().pipe(filter((p) => !!p));
      const category$ = this.categoryService.getCurrentCategory().pipe(filter((c) => c !== undefined));
      const product$ = this.currentProductService.getProduct();

      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          distinctUntilChanged((prev: NavigationEnd, curr: NavigationEnd) => {
            const prevParsedUrl = this.router.parseUrl(prev.url);
            const currParsedUrl = this.router.parseUrl(curr.url);
            return areSegmentsEqual(prevParsedUrl, currParsedUrl);
          }),
          delay(500),
          switchMap(() => combineLatest([page$, category$, product$]).pipe(take(1))),
          takeUntil(this.destroy$)
        )
        .subscribe(([page, category, product]) => {
          this.showChatToUser = this.showLiveAgentChat(page, category, product);
          if (this.showChatToUser) {
            this.isMidlyOpenChat(baseStore);
            this.liveChatAgentConfig = this.showLiveChatAgentConfigs(baseStore, page, category, product);
            this.initLiveChatAgentScript();
          }
        });
    }
  }

  initESW(alreadyLoaded: boolean): void {
    // @ts-expect-error: EmbeddedSVC JS Object
    this.embeddedSvc = embedded_svc;
    this.embeddedSvc.settings.extraPrechatFormDetails = [{ label: 'Text', transcriptFields: ['UrlSourcePage__c'] }];
    this.embeddedSvc.settings.displayHelpButton = true;
    this.embeddedSvc.settings.language = this.liveChatAgentConfig?.languages;
    this.embeddedSvc.settings.targetElement = this.document.getElementById('liveChatCta');

    this.embeddedSvc.settings.defaultAssistiveText = ' ';
    this.embeddedSvc.settings.loadingText = this.translateService.instant('chat.loadingText');
    this.embeddedSvc.settings.defaultMinimizedText = this.translateService.instant('chat.minimizedText');
    this.embeddedSvc.settings.disabledMinimizedText = this.translateService.instant('chat.disabledMinimizedText');
    this.embeddedSvc.settings.offlineSupportMinimizedText = this.translateService.instant(
      'chat.offlineSupportMinimizedText'
    );
    const chatbotAvatarImgURL = this.translateService.instant('chat.chatbotAvatarImgURL');
    const avatarImgURL = this.translateService.instant('chat.avatarImgURL');
    this.embeddedSvc.settings.chatbotAvatarImgURL =
      chatbotAvatarImgURL !== 'chat.chatbotAvatarImgURL' ? chatbotAvatarImgURL : null;
    this.embeddedSvc.settings.avatarImgURL = avatarImgURL !== 'chat.avatarImgURL' ? avatarImgURL : null;

    this.embeddedSvc.loadCSS = () => undefined;

    this.embeddedSvc.settings.enabledFeatures = ['LiveAgent'];
    this.embeddedSvc.settings.entryFeature = 'LiveAgent';

    this.embeddedSvc.init(
      this.liveChatAgentConfig?.baseCoreURL,
      this.liveChatAgentConfig?.communityEndpointURL,
      alreadyLoaded ? this.liveChatAgentConfig?.gslbBaseURL : null,
      this.liveChatAgentConfig?.deploymentOrg,
      this.liveChatAgentConfig?.typeParameter,
      {
        baseLiveAgentContentURL: this.liveChatAgentConfig?.baseLiveAgentContentURL,
        deploymentId: this.liveChatAgentConfig?.deploymentId,
        buttonId: this.liveChatAgentConfig?.buttonId,
        baseLiveAgentURL: this.liveChatAgentConfig?.endpointUrl,
        eswLiveAgentDevName: this.liveChatAgentConfig?.eswLiveAgentDevName,
        isOfflineSupportEnabled: this.liveChatAgentConfig?.isOfflineSupportEnabled
      }
    );
  }

  initChatUser(): void {
    const openButton: HTMLElement = this.document.getElementById('liveChatPicto');
    const chatBlock: HTMLElement = this.document.getElementById('liveChatBlock');
    const liveChatCta: HTMLElement = this.document.getElementById('liveChatCta');
    const chatBlockParent: HTMLElement = chatBlock.parentElement;

    const callback = () => {
      const isLiveChatOpen: boolean = this.document.querySelector('#liveChatBlock .embeddedServiceSidebar') !== null;
      const isLiveChatDockableContainer: boolean =
        this.document.querySelector('#liveChatBlock .sidebarMinimized') !== null;
      if (this.document.querySelector('#liveChatBlock .helpButtonEnabled') !== null) {
        chatBlockParent.style.display = '';
        if (isLiveChatOpen) {
          openButton.click();
        }
      } else if (this.document.querySelector('#liveChatBlock .helpButtonDisabled') !== null) {
        if (isLiveChatOpen) {
          chatBlockParent.style.display = '';
        } else {
          chatBlockParent.style.display = 'none';
        }
      }
      if (isLiveChatOpen && isLiveChatDockableContainer) {
        if (this.chatComponent) {
          this.renderer.setStyle(this.chatComponent?.nativeElement, 'width', '0');
          this.renderer.setStyle(this.chatComponent?.nativeElement, 'height', '0');
        }
      } else {
        if (this.chatComponent) {
          this.renderer.setStyle(this.chatComponent?.nativeElement, 'width', 'inherit');
          this.renderer.setStyle(this.chatComponent?.nativeElement, 'height', 'inherit');
        }
      }
      this.cdRef.markForCheck();
    };

    this.liveChatObserver = new MutationObserver(callback);
    const options = {
      childList: true,
      subtree: true,
      attributes: true
    };

    this.liveChatObserver.observe(liveChatCta, options);
  }

  initLiveChatAgentScript(): void {
    // @ts-expect-error: EmbeddedSVC JS Object
    if (typeof embedded_svc === 'undefined') {
      this.scriptLoader.embedScript({
        src: this.liveChatAgentConfig?.eswJsUrl,
        callback: () => {
          waitForElm('#liveChatBlock').then(() => {
            this.initESW(false);
            this.initChatUser();
          });
        }
      });
    }
  }

  public showLiveAgentChat(page: Page, category: CustomCategory, product: Product): boolean {
    if (product) {
      return product.showChat;
    }
    if (category) {
      return category.showChat;
    }
    return page['showChat'];
  }

  showLiveChatAgentConfigs(
    store: CustomBaseStore,
    page: Page,
    category: CustomCategory,
    product: Product
  ): LiveChatAgentConfig {
    if (product?.liveChatAgentConfig?.code) {
      return product.liveChatAgentConfig;
    }
    if (category?.liveChatAgentConfig?.code) {
      return category.liveChatAgentConfig;
    }
    if (page?.['liveChatAgentConfig']?.code) {
      return page['liveChatAgentConfig'];
    }
    return store?.liveChatAgentConfig;
  }

  isMidlyOpenChat(store: CustomBaseStore): void {
    if (!!store && isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.isMidlyOpen = true;
        this.cdRef.markForCheck();
      }, store?.liveChatDelay);

      setTimeout(() => {
        this.isMidlyOpen = false;
        this.cdRef.markForCheck();
      }, store?.liveChatDuration);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.liveChatObserver?.disconnect();
  }

  trackChat(type: ChatActionsEnum): void {
    if (type === ChatActionsEnum.LAUNCH) {
      if (this.keypressed) {
        this.messageLen++;
        this.keypressed = false;
      }
      return;
    }
    this.trackingService.sendEvent('chatClick', { chatAction: type });
  }

  sendChatMessage(): void {
    if (this.messageLen > 0) {
      this.trackingService.sendEvent('chatClick', { chatAction: ChatActionsEnum.LAUNCH });
      this.messageLen = 0;
    }
  }

  keyPressed(): void {
    this.keypressed = true;
  }
}
